import React from 'react';

/**
 * Read-only, compact Babylon contract card surfaced inline in the public /coach
 * chat when the coach names a specific option contract and the reader taps it.
 * Mirrors PublicStockCard's aesthetic (dark ledger, mono numerals, blue spine).
 * Presentation only — no actions, no "Add to Portfolio" CTA (a later phase).
 * All fields are nullable (API-only data policy) and render "—" when absent.
 */

export interface PublicContractCardData {
  symbol: string;
  contract_type: 'call' | 'put';
  strike: number | null;
  expiry: string | null; // ISO date or null
  days_to_expiration: number | null;
  underlying_price: number | null;
  otm_pct: number | null;
  premium: number | null;
  ann_return_pct: number | null;
  delta: number | null;
  implied_volatility: number | null; // 0–1 fraction
  open_interest: number | null;
  volume: number | null;
}

const DASH = '—';

const fmtMoney = (v: number | null): string =>
  v == null ? DASH : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtPct = (v: number | null, sign = false): string => {
  if (v == null) return DASH;
  const s = `${Math.abs(v) < 100 ? v.toFixed(1) : v.toFixed(0)}%`;
  return sign && v >= 0 ? `+${s}` : s;
};

const fmtNum = (v: number | null): string =>
  v == null ? DASH : v.toLocaleString();

const fmtDelta = (v: number | null): string =>
  v == null ? DASH : v.toFixed(2);

const fmtDate = (iso: string | null): string => {
  if (!iso) return DASH;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return DASH;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** A simple, honest risk read derived from |delta| (assignment likelihood). */
const riskRead = (delta: number | null): { label: string; cls: string } | null => {
  if (delta == null) return null;
  const d = Math.abs(delta);
  if (d < 0.3) return { label: 'Lower assignment odds', cls: 'pcc-risk--low' };
  if (d <= 0.6) return { label: 'Moderate assignment odds', cls: 'pcc-risk--mid' };
  return { label: 'Higher assignment odds', cls: 'pcc-risk--high' };
};

const Stat: React.FC<{ label: string; value: React.ReactNode; green?: boolean }> = ({
  label,
  value,
  green,
}) => (
  <div className="pcc-stat">
    <span className="pcc-stat-label">{label}</span>
    <span className={`pcc-stat-val${green ? ' pcc-stat-val--green' : ''}`}>{value}</span>
  </div>
);

const PublicContractCard: React.FC<{ data: PublicContractCardData }> = ({ data }) => {
  const isCall = data.contract_type === 'call';
  const risk = riskRead(data.delta);
  const ivPct = data.implied_volatility == null ? null : data.implied_volatility * 100;

  return (
    <div
      className="pcc-card"
      role="group"
      aria-label={`${data.symbol} ${data.contract_type} contract card`}
    >
      {/* Header — strike + CALL/PUT badge */}
      <div className="pcc-head">
        <div className="pcc-id">
          <span className="pcc-symbol">{data.symbol}</span>
          <span className="pcc-strike">{fmtMoney(data.strike)}</span>
        </div>
        <span className={`pcc-badge${isCall ? ' pcc-badge--call' : ' pcc-badge--put'}`}>
          {isCall ? 'CALL' : 'PUT'}
        </span>
      </div>

      {/* Expiry + DTE + %OTM */}
      <div className="pcc-sub">
        <span className="pcc-sub-item">{fmtDate(data.expiry)}</span>
        <span className="pcc-sub-dot" aria-hidden="true">·</span>
        <span className="pcc-sub-item">
          {data.days_to_expiration == null ? DASH : `${data.days_to_expiration}d`}
        </span>
        <span className="pcc-sub-dot" aria-hidden="true">·</span>
        <span className="pcc-sub-item">{fmtPct(data.otm_pct)} OTM</span>
      </div>

      {/* Premium + annualized return — the headline economics (green) */}
      <div className="pcc-premium">
        <div className="pcc-premium-main">
          <span className="pcc-premium-label">Premium</span>
          <span className="pcc-premium-val">{fmtMoney(data.premium)}</span>
        </div>
        <div className="pcc-premium-ann">
          <span className="pcc-premium-label">Annualized</span>
          <span className="pcc-premium-ann-val">{fmtPct(data.ann_return_pct)}</span>
        </div>
      </div>

      {/* Secondary stats */}
      <div className="pcc-stats">
        <Stat label="Delta" value={fmtDelta(data.delta)} />
        <Stat label="IV" value={fmtPct(ivPct)} />
        <Stat label="Open interest" value={fmtNum(data.open_interest)} />
        <Stat label="Underlying" value={fmtMoney(data.underlying_price)} />
      </div>

      {/* Honest risk read from delta */}
      {risk && (
        <div className={`pcc-risk ${risk.cls}`}>
          <span className="pcc-risk-dot" aria-hidden="true" />
          {risk.label}
        </div>
      )}

      <div className="pcc-foot">via Babylon</div>
    </div>
  );
};

/** Slim skeleton shown while a contract card is fetching. */
export const PublicContractCardSkeleton: React.FC = () => (
  <div className="pcc-card pcc-card--skel" aria-hidden="true">
    <div className="psc-skel-line psc-skel-line--head" />
    <div className="psc-skel-line psc-skel-line--price" />
    <div className="psc-skel-line psc-skel-line--bar" />
    <div className="psc-skel-line psc-skel-line--bar" />
  </div>
);

/** Quiet inline notice when a contract card can't load (non-404 error). */
export const PublicContractCardError: React.FC = () => (
  <div className="pcc-card pcc-card--error">Couldn't load that contract right now.</div>
);

export default PublicContractCard;
